/* OS Listicle - Offer Card */
(() => {
  const SECTION_SELECTOR = '.os-listicle-offer-card';
  const CTA_SELECTOR = '[data-os-listicle-offer-cta]';
  const TIMER_SELECTOR = '[data-os-listicle-offer-timer]';
  const BOGO_RELOAD_MARKER = '_os_listicle_bogo_reload';

  function normalizeDiscountCodes(rawCodes) {
    return String(rawCodes || '')
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean)
      .join(',');
  }

  function normalizeBogoParamString(rawValue) {
    if (!rawValue) return '';

    let normalized = String(rawValue).trim();
    if (!normalized) return '';

    if (normalized.includes('://')) {
      try {
        normalized = new URL(normalized).search.replace(/^\?/, '');
      } catch (error) {}
    }

    const queryStartIndex = normalized.indexOf('?');
    if (queryStartIndex > -1) {
      normalized = normalized.slice(queryStartIndex + 1);
    }

    normalized = normalized.replace(/^[?&]+/, '');

    const hashIndex = normalized.indexOf('#');
    if (hashIndex > -1) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized.trim();
  }

  function isInternalBogoParam(key) {
    return key === BOGO_RELOAD_MARKER;
  }

  function getBogoConfig(section) {
    return {
      enabled:
        !!section &&
        section.getAttribute('data-os-listicle-bogo-url-params-enabled') === 'true',
      params:
        section && section.getAttribute('data-os-listicle-bogo-url-params')
          ? section.getAttribute('data-os-listicle-bogo-url-params')
          : '',
      checkoutUrl:
        section && section.getAttribute('data-os-listicle-checkout-url')
          ? section.getAttribute('data-os-listicle-checkout-url')
          : '/checkout',
    };
  }

  function getActiveBogoParams(section) {
    const config = getBogoConfig(section);
    if (!config.enabled) return '';

    return normalizeBogoParamString(config.params);
  }

  function configuredBogoParamsMatchUrl(sanitizedParams) {
    const incoming = new URLSearchParams(sanitizedParams);
    const current = new URLSearchParams(window.location.search);
    let matches = true;

    incoming.forEach((value, key) => {
      if (!key) return;

      if (current.get(key) !== value) {
        matches = false;
      }
    });

    return matches;
  }

  function mergeBogoParamsIntoUrl(baseUrl, sanitizedParams) {
    const mergedUrl = new URL(baseUrl, window.location.origin);
    const incomingParams = new URLSearchParams(sanitizedParams);
    let hasValidParam = false;

    incomingParams.forEach((value, key) => {
      if (!key) return;

      mergedUrl.searchParams.set(key, value);
      hasValidParam = true;
    });

    return hasValidParam ? mergedUrl : null;
  }

  function stripInternalBogoParamsFromCurrentUrl() {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(BOGO_RELOAD_MARKER)) return;

    url.searchParams.delete(BOGO_RELOAD_MARKER);

    const nextRelativeUrl =
      url.pathname + (url.search || '') + url.hash;

    window.history.replaceState({}, '', nextRelativeUrl);
  }

  function applyBogoUrlParams(section, options = {}) {
    const sanitizedParams = getActiveBogoParams(section);
    const allowReload = !!options.allowReload;

    if (!sanitizedParams) return false;

    if (configuredBogoParamsMatchUrl(sanitizedParams)) {
      stripInternalBogoParamsFromCurrentUrl();
      return false;
    }

    const mergedUrl = mergeBogoParamsIntoUrl(
      window.location.href,
      sanitizedParams
    );

    if (!mergedUrl) return false;

    if (allowReload) {
      const currentUrl = new URL(window.location.href);

      if (!currentUrl.searchParams.has(BOGO_RELOAD_MARKER)) {
        mergedUrl.searchParams.set(BOGO_RELOAD_MARKER, '1');
        window.location.replace(mergedUrl.toString());
        return true;
      }
    }

    mergedUrl.searchParams.delete(BOGO_RELOAD_MARKER);

    const nextRelativeUrl =
      mergedUrl.pathname +
      (mergedUrl.search || '') +
      mergedUrl.hash;

    window.history.replaceState({}, '', nextRelativeUrl);

    return false;
  }

  function extractBogoCartAttributes(section) {
    const sanitizedParams = getActiveBogoParams(section);
    if (!sanitizedParams) return null;

    const attributes = {};
    let hasAny = false;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      attributes[key] = value;
      hasAny = true;
    });

    return hasAny ? attributes : null;
  }

  function copyPublicSearchParams(sourceParams, targetUrl) {
    sourceParams.forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      targetUrl.searchParams.set(key, value);
    });
  }

  function mergeConfiguredBogoParams(checkoutUrl, section) {
    const sanitizedParams = getActiveBogoParams(section);
    if (!sanitizedParams) return;

    new URLSearchParams(sanitizedParams).forEach((value, key) => {
      if (!key || isInternalBogoParam(key)) return;

      checkoutUrl.searchParams.set(key, value);
    });
  }

  function getBogoCheckoutUrl(button, section) {
    const config = getBogoConfig(section);
    const checkoutUrl = new URL(
      config.checkoutUrl || '/checkout',
      window.location.origin
    );

    copyPublicSearchParams(
      new URLSearchParams(window.location.search),
      checkoutUrl
    );

    mergeConfiguredBogoParams(checkoutUrl, section);

    const discountCodes = normalizeDiscountCodes(
      button.getAttribute('data-discount-codes')
    );

    if (discountCodes) {
      checkoutUrl.searchParams.set('discount', discountCodes);
    }

    if (checkoutUrl.origin === window.location.origin) {
      return (
        checkoutUrl.pathname +
        checkoutUrl.search +
        checkoutUrl.hash
      );
    }

    return checkoutUrl.toString();
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, options);

    if (!response.ok) {
      let message = 'Something went wrong. Please try again.';

      try {
        const data = await response.json();
        message = data.description || data.message || message;
      } catch (error) {}

      throw new Error(message);
    }

    return response.json();
  }

  async function clearCart() {
    await fetchJson('/cart/clear.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });
  }

  function getMainQuantity(button) {
    const quantity = parseInt(
      button.getAttribute('data-quantity') || '1',
      10
    );

    return Number.isFinite(quantity) && quantity > 0
      ? quantity
      : 1;
  }

  async function addMainItem(button) {
    const variantId = button.getAttribute('data-variant-id');
    const sellingPlanId = button.getAttribute('data-selling-plan-id');
    const quantity = getMainQuantity(button);

    if (!variantId) {
      throw new Error('Missing variant ID.');
    }

    const item = {
      id: Number(variantId),
      quantity,
    };

    if (sellingPlanId) {
      item.selling_plan = Number(sellingPlanId);
    }

    await fetchJson('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [item],
      }),
    });
  }

  async function fetchCartForBogo() {
    return fetchJson('/cart.js', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      cache: 'no-store',
    });
  }

  async function updateBogoCartAttributes(attributes) {
    if (!attributes) return null;

    return fetchJson('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        attributes,
      }),
      credentials: 'same-origin',
      cache: 'no-store',
    });
  }

  function waitForBogoGift(knownItemCount) {
    return new Promise((resolve) => {
      let elapsed = 0;
      const interval = 200;
      const maxWait = 3000;

      const checkCart = async () => {
        try {
          const cart = await fetchCartForBogo();

          if (
            cart &&
            typeof cart.item_count === 'number' &&
            cart.item_count > knownItemCount
          ) {
            resolve(true);
            return;
          }
        } catch (error) {
          resolve(false);
          return;
        }

        elapsed += interval;

        if (elapsed >= maxWait) {
          resolve(false);
          return;
        }

        window.setTimeout(checkCart, interval);
      };

      window.setTimeout(checkCart, interval);
    });
  }

  function redirectToCheckout(button) {
    const discountCodes = normalizeDiscountCodes(button.getAttribute('data-discount-codes'));
    let checkoutUrl = '/checkout';

    if (discountCodes) {
      checkoutUrl += `?discount=${encodeURIComponent(discountCodes)}`;
    }

    window.location.href = checkoutUrl;
  }

  function redirectToBogoCheckout(button, section) {
    window.location.href = getBogoCheckoutUrl(
      button,
      section
    );
  }

  function redirectToAssignedLink(button) {
    const linkUrl = button.getAttribute('data-link-url');
    const openNewTab = button.getAttribute('data-link-new-tab') === '1';

    if (!linkUrl) {
      showError(button, 'Missing assigned link URL.');
      return;
    }

    if (openNewTab) {
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.href = linkUrl;
  }

  function showError(button, message) {
    const block = button.closest('.os-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-os-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function hideError(button) {
    const block = button.closest('.os-listicle-offer-card__block--cta');
    const errorEl = block ? block.querySelector('[data-os-listicle-offer-error]') : null;

    if (!errorEl) return;

    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  async function handleCtaClick(button) {
    if (!button || button.classList.contains('is-loading')) return;

    const section = button.closest(SECTION_SELECTOR);
    const ctaBehavior = button.getAttribute('data-cta-behavior') || 'checkout';
    const originalText = button.textContent;
    const loadingText = button.getAttribute('data-loading-text') || 'ADDING...';
    const shouldClearCart = button.getAttribute('data-clear-cart') === '1';
    const bogoParams = getActiveBogoParams(section);
    const bogoActive = !!bogoParams;

    hideError(button);

    if (ctaBehavior === 'link') {
      redirectToAssignedLink(button);
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;
    button.textContent = loadingText;

    try {
      if (!bogoActive) {
        if (shouldClearCart) {
          await clearCart();
        }

        await addMainItem(button);
        redirectToCheckout(button);
        return;
      }

      applyBogoUrlParams(section);

      if (shouldClearCart) {
        await clearCart();
        await new Promise((resolve) => {
          window.setTimeout(resolve, 180);
        });
      }

      let cartBeforeAdd = null;

      try {
        cartBeforeAdd = await fetchCartForBogo();
      } catch (error) {
        cartBeforeAdd = null;
      }

      await addMainItem(button);

      let cartAfterAdd = null;

      try {
        cartAfterAdd = await fetchCartForBogo();
      } catch (error) {
        cartAfterAdd = null;
      }

      const bogoAttributes = extractBogoCartAttributes(section);
      let cartAfterAttributeUpdate = null;

      if (bogoAttributes) {
        cartAfterAttributeUpdate = await updateBogoCartAttributes(
          bogoAttributes
        );
      }

      if (bogoAttributes) {
        const latestCart =
          cartAfterAttributeUpdate ||
          cartAfterAdd ||
          null;

        const knownItemCount =
          latestCart &&
          typeof latestCart.item_count === 'number'
            ? latestCart.item_count
            : null;

        const baselineItemCount =
          cartBeforeAdd &&
          typeof cartBeforeAdd.item_count === 'number'
            ? cartBeforeAdd.item_count
            : null;

        const mainQuantity = getMainQuantity(button);

        const giftAlreadyPresent =
          knownItemCount !== null &&
          baselineItemCount !== null &&
          knownItemCount > baselineItemCount + mainQuantity;

        if (!giftAlreadyPresent && knownItemCount !== null) {
          await waitForBogoGift(knownItemCount);
        }
      }

      applyBogoUrlParams(section);
      redirectToBogoCheckout(button, section);
    } catch (error) {
      showError(button, error.message || 'Something went wrong. Please try again.');

      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function initTimer(timer) {
    if (!timer || timer.__osListicleOfferTimerBound) return;
    timer.__osListicleOfferTimerBound = true;

    let durationMinutes = parseInt(timer.getAttribute('data-duration-minutes'), 10);
    const storageKey = timer.getAttribute('data-storage-key');

    const hoursEl = timer.querySelector('[data-os-listicle-offer-hours]');
    const minsEl = timer.querySelector('[data-os-listicle-offer-mins]');
    const secsEl = timer.querySelector('[data-os-listicle-offer-secs]');

    if (!durationMinutes || durationMinutes < 1) {
      durationMinutes = 1;
    }

    let endTime = null;
    const now = Date.now();

    try {
      endTime = parseInt(window.localStorage.getItem(storageKey), 10);
    } catch (error) {
      endTime = null;
    }

    if (!endTime || endTime <= now) {
      endTime = now + durationMinutes * 60 * 1000;

      try {
        window.localStorage.setItem(storageKey, String(endTime));
      } catch (error) {}
    }

    function renderTimer() {
      let remaining = endTime - Date.now();

      if (remaining <= 0) {
        remaining = 0;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minsEl) minsEl.textContent = pad(minutes);
      if (secsEl) secsEl.textContent = pad(seconds);

      if (remaining > 0) {
        window.setTimeout(renderTimer, 1000);
      }
    }

    renderTimer();
  }

  function initSection(section) {
    if (!section || section.__osListicleOfferBound) return;
    section.__osListicleOfferBound = true;

    section.querySelectorAll(TIMER_SELECTOR).forEach(initTimer);

    section.addEventListener('click', (event) => {
      const button = event.target.closest(CTA_SELECTOR);
      if (!button || !section.contains(button)) return;

      event.preventDefault();
      handleCtaClick(button);
    });
  }

  function init() {
    const sections = Array.from(
      document.querySelectorAll(SECTION_SELECTOR)
    );

    const bogoSection = sections.find(
      (section) => !!getActiveBogoParams(section)
    );

    if (
      bogoSection &&
      applyBogoUrlParams(
        bogoSection,
        { allowReload: true }
      )
    ) {
      return;
    }

    sections.forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState !== 'loading') {
    init();
  }
})();
